/**
 * Static flowchart catalogue stub.
 *
 * The full registry lives in `screens/src/data/flowcharts/` (~10 modules,
 * ~80 diagrams) and renders via the `mermaid` runtime. mermaid is not yet
 * wired in web-staff; we render the source as a `<pre>` block and surface a
 * "Diagrams require build step" notice. The list below is a curated subset
 * representing one diagram per module so the IA stays browsable.
 */
export interface FlowchartModule {
  readonly slug: string;
  readonly label: string;
  readonly order: number;
}

export interface FlowchartEntry {
  readonly id: string;
  readonly module: string;
  readonly title: string;
  readonly description: string;
  readonly featureIds: readonly string[];
  readonly actors: readonly string[];
  readonly systems: readonly string[];
  readonly mermaid: string;
}

export const MODULES: readonly FlowchartModule[] = [
  { slug: 'overview',  label: 'Cross-Cutting Processes', order: 0 },
  { slug: 'mail',      label: 'Mail & Parcels',          order: 1 },
  { slug: 'finance',   label: 'Payments & Finance',      order: 2 },
  { slug: 'printing',  label: 'Printing & Labels',       order: 3 },
  { slug: 'tracking',  label: 'Tracking & Customer',     order: 4 },
  { slug: 'delivery',  label: 'Delivery & POD',          order: 5 },
  { slug: 'franchise', label: 'Franchise Onboarding',    order: 6 },
  { slug: 'kyc',       label: 'Identity & KYC',          order: 7 },
  { slug: 'security',  label: 'Security & Audit',        order: 8 },
  { slug: 'reporting', label: 'Reporting & Dashboards',  order: 9 },
];

export const FLOWCHARTS: readonly FlowchartEntry[] = [
  {
    id: 'overview-mvp-acceptance',
    module: 'overview',
    title: 'MVP acceptance overview',
    description: 'High-level traceability from acceptance criteria to evidence pages.',
    featureIds: ['F-MVP'],
    actors: ['Programme office', 'Module leads'],
    systems: ['UPMP web-staff'],
    mermaid: `flowchart TD\n  A((Start)) --> B[Track 14 criteria]\n  B --> C{Status?}\n  C -->|Pass| D[Evidence link]\n  C -->|In Progress| E[Owner update]\n  C -->|Not Started| F[Schedule]\n  D --> G((End))\n  E --> G\n  F --> G`,
  },
  {
    id: 'mail-registration',
    module: 'mail',
    title: 'Mail registration',
    description: 'Counter capture, pricing, payment, label print.',
    featureIds: ['F-003', 'F-004', 'F-005'],
    actors: ['Counter staff', 'Customer'],
    systems: ['Pricing engine', 'Payment gateway'],
    mermaid: `flowchart TD\n  A((Start)) --> B[Capture sender + recipient]\n  B --> C[Quote pricing]\n  C --> D[Take payment]\n  D --> E[Print label]\n  E --> F((End))`,
  },
  {
    id: 'finance-recipient-account-mgmt',
    module: 'finance',
    title: 'Recipient account management',
    description: 'NIBSS resolve + penny-drop + activation.',
    featureIds: ['F-FIN-RA'],
    actors: ['Finance admin'],
    systems: ['NIBSS gateway'],
    mermaid: `flowchart TD\n  A((Start)) --> B[Submit account]\n  B --> C[NIBSS resolve]\n  C --> D{Match >= 85?}\n  D -->|Yes| E[Penny drop]\n  D -->|No| F[Reject]\n  E --> G((End))`,
  },
  {
    id: 'finance-revenue-share-engine',
    module: 'finance',
    title: 'Revenue share engine',
    description: 'Pre-deductions, splits and residual recipient.',
    featureIds: ['F-135'],
    actors: ['Finance admin'],
    systems: ['Settlement engine'],
    mermaid: `flowchart TD\n  A((Gross)) --> B[Pre-deduct]\n  B --> C[Split lines]\n  C --> D[Residual]\n  D --> E((End))`,
  },
  {
    id: 'tracking-sms-notifications',
    module: 'tracking',
    title: 'Tracking SMS notifications',
    description: 'Outbound SMS lifecycle from registration to delivery.',
    featureIds: ['F-NOT-001'],
    actors: ['Customer'],
    systems: ['Termii', 'service-notification'],
    mermaid: `flowchart LR\n  A[Registered] --> B[Dispatched]\n  B --> C[Out for delivery]\n  C --> D[Delivered]`,
  },
  {
    id: 'delivery-pod-capture',
    module: 'delivery',
    title: 'Proof of delivery capture',
    description: 'Driver captures POD signature + photo + GPS.',
    featureIds: ['F-DEL-POD'],
    actors: ['Driver', 'Recipient'],
    systems: ['Mobile POD app'],
    mermaid: `flowchart TD\n  A[Arrive] --> B[Capture signature]\n  B --> C[Capture photo]\n  C --> D[Sync]`,
  },
  {
    id: 'franchise-provisioning',
    module: 'franchise',
    title: 'Franchise provisioning',
    description: 'POS + printer + scanner provisioning per outlet.',
    featureIds: ['F-FRA-PROV'],
    actors: ['Franchise ops'],
    systems: ['Device fleet'],
    mermaid: `flowchart TD\n  A[Outlet approved] --> B[Order devices]\n  B --> C[Configure]\n  C --> D[Activate]`,
  },
  {
    id: 'kyc-nin-verify',
    module: 'kyc',
    title: 'NIN verification',
    description: 'NIMC NIN lookup + biometric match.',
    featureIds: ['F-KYC-NIN'],
    actors: ['Counter staff', 'Customer'],
    systems: ['NIMC API'],
    mermaid: `flowchart TD\n  A[Capture NIN] --> B[NIMC lookup]\n  B --> C{Match?}\n  C -->|Yes| D[Verified]\n  C -->|No| E[Manual review]`,
  },
  {
    id: 'security-audit-log',
    module: 'security',
    title: 'Audit log',
    description: 'Append-only audit trail for sensitive operations.',
    featureIds: ['F-SEC-AUD'],
    actors: ['All operator actions'],
    systems: ['service-audit'],
    mermaid: `flowchart LR\n  A[Action] --> B[Sign]\n  B --> C[Append to chain]`,
  },
  {
    id: 'reporting-daily-rollup',
    module: 'reporting',
    title: 'Daily transaction rollup',
    description: 'Cron job aggregates the day\'s transactions to PDF + CSV.',
    featureIds: ['F-REP-DAILY'],
    actors: ['Reporting service'],
    systems: ['service-reporting'],
    mermaid: `flowchart TD\n  A[00:30 cron] --> B[Aggregate]\n  B --> C[Render PDF]\n  C --> D[Email]`,
  },
];

export function groupByModule(): Record<string, { module: FlowchartModule; items: FlowchartEntry[] }> {
  const out: Record<string, { module: FlowchartModule; items: FlowchartEntry[] }> = {};
  for (const m of MODULES) out[m.slug] = { module: m, items: [] };
  for (const fc of FLOWCHARTS) {
    out[fc.module]?.items.push(fc);
  }
  return out;
}

export function getFlowchartById(id: string): FlowchartEntry | undefined {
  return FLOWCHARTS.find((fc) => fc.id === id);
}
